'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { Checkbox } from '@kit/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import {
  DANCE_POSITIONS,
  GENDER_OPTIONS,
} from '../../constants/audition.constants';
import type { UpdateAuditionInput } from '../../schemas/audition.schema';

interface RequirementsTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
}

export function RequirementsTab({ form }: RequirementsTabProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Gender Requirements */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.requirements.gender.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.requirements.gender.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="requirementGenders"
            render={() => (
              <FormItem>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {GENDER_OPTIONS.map((option) => (
                    <FormField
                      key={option.value}
                      control={form.control}
                      name="requirementGenders"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(option.value)}
                              onCheckedChange={(checked) => {
                                const current = field.value || [];
                                if (checked) {
                                  field.onChange([...current, option.value]);
                                } else {
                                  field.onChange(
                                    current.filter((v) => v !== option.value),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <FormLabel className="cursor-pointer font-normal">
                            {option.label}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Age Requirements */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.requirements.age.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.requirements.age.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={form.control}
              name="requirementAgeMin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.requirements.minAge" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g., 18"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requirementAgeMax"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="auditions:formTabs.requirements.maxAge" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g., 30"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Height Requirements */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.requirements.height.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.requirements.height.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Male Height */}
          <div>
            <h4 className="mb-3 text-sm font-medium">
              <Trans i18nKey="auditions:formTabs.requirements.height.male" />
            </h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="requirementHeightMaleMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="auditions:formTabs.requirements.minimum" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={100}
                        max={250}
                        placeholder="e.g., 175"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="requirementHeightMaleMax"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="auditions:formTabs.requirements.maximum" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={100}
                        max={250}
                        placeholder="e.g., 190"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Female Height */}
          <div>
            <h4 className="mb-3 text-sm font-medium">
              <Trans i18nKey="auditions:formTabs.requirements.height.female" />
            </h4>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="requirementHeightFemaleMin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="auditions:formTabs.requirements.minimum" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={100}
                        max={250}
                        placeholder="e.g., 160"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="requirementHeightFemaleMax"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans i18nKey="auditions:formTabs.requirements.maximum" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={100}
                        max={250}
                        placeholder="e.g., 175"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dance Positions */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.requirements.positions.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.requirements.positions.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="requirementDancePositions"
            render={() => (
              <FormItem>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {DANCE_POSITIONS.map((position) => (
                    <FormField
                      key={position.value}
                      control={form.control}
                      name="requirementDancePositions"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-y-0 space-x-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(position.value)}
                              onCheckedChange={(checked) => {
                                const current = field.value || [];
                                if (checked) {
                                  field.onChange([...current, position.value]);
                                } else {
                                  field.onChange(
                                    current.filter((v) => v !== position.value),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <FormLabel className="cursor-pointer font-normal">
                            {position.label}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Other Requirements */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:formTabs.requirements.other.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:formTabs.requirements.other.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="requirementOther"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder={t(
                      'auditions:formTabs.requirements.other.placeholder',
                    )}
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
